import React, {
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import MuiStepper from '@material-ui/core/Stepper';
import {Button, Step, StepContent, StepLabel} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import Loader from '@components/framework/loader/Loader';
import './Stepper.scss';

interface IStepItem {
  title: ReactNode;
  nextButtonLabel?: ReactNode;
  backButtonLabel?: ReactNode;
  content: ReactNode;
  nextButtonLoadingState?: boolean;
  nextButtonDisabledState?: boolean;
  onNextButtonClick?: () => Promise<boolean> | boolean;
  onBackButtonClick?: () => Promise<boolean> | boolean;
  hideBackButton?: boolean;
}

interface IStepperProps {
  steps: IStepItem[];
  onStepChange?: (activeStep: number) => void;
}

const Stepper = React.forwardRef<{goToNextStep: () => void}, IStepperProps>(
  (props, ref): React.ReactElement => {
    const {steps, onStepChange} = props;
    const [activeStep, setActiveStep] = useState(0);

    const {t} = useTranslation();
    const isLastStep = () => {
      return activeStep === steps.length - 1;
    };

    const isFirstStep = () => {
      return activeStep === 0;
    };

    const goToNextStep = () => {
      if (!isLastStep()) {
        setActiveStep(prevStep => prevStep + 1);
      }
    };

    const goToPrevStep = () => {
      if (!isFirstStep()) {
        setActiveStep(prevStep => prevStep - 1);
      }
    };

    useImperativeHandle(ref, () => {
      return {
        goToNextStep
      };
    });

    useEffect(() => {
      if (onStepChange) {
        onStepChange(activeStep);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    const activeStepItem = steps[activeStep];

    const renderDesktopStepper = () => {
      return (
        <div className="stepper desktop-stepper">
          <MuiStepper
            activeStep={activeStep}
            className="stepper-titles-wrapper">
            {steps.map((step, idx) => (
              <Step key={idx}>
                <StepLabel className={'step-label'}>{step.title}</StepLabel>
              </Step>
            ))}
          </MuiStepper>
          <div className="stepper-content">{activeStepItem.content}</div>
          <div className="steps-navigation-button-wrapper">
            {activeStepItem.hideBackButton ? null : (
              <Button
                className="stepper-navigation-button"
                onClick={async () => {
                  if (activeStepItem?.onBackButtonClick) {
                    const shouldMoveToPrevStep =
                      await activeStepItem.onBackButtonClick();
                    if (shouldMoveToPrevStep) {
                      goToPrevStep();
                    }
                  } else {
                    goToPrevStep();
                  }
                }}>
                {activeStepItem?.backButtonLabel ?? t('COMMON.BACK')}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              className="stepper-navigation-button next-button"
              onClick={async () => {
                if (activeStepItem?.onNextButtonClick) {
                  const shouldMoveToNextStep =
                    await activeStepItem.onNextButtonClick();
                  if (shouldMoveToNextStep) {
                    goToNextStep();
                  }
                } else {
                  goToNextStep();
                }
              }}
              disabled={activeStepItem?.nextButtonDisabledState}
              startIcon={
                activeStepItem?.nextButtonLoadingState ? (
                  <Loader size={'1rem'} color={'inherit'} />
                ) : null
              }>
              {activeStepItem?.nextButtonLabel ?? t('COMMON.NEXT')}
            </Button>
          </div>
        </div>
      );
    };

    const renderMobileStepper = () => {
      return (
        <MuiStepper
          activeStep={activeStep}
          orientation="vertical"
          className={'stepper mobile-stepper'}>
          {steps.map((step, index) => {
            return (
              <Step key={index}>
                <StepLabel className={'step-label'}>{step.title}</StepLabel>
                <StepContent className={'stepper-content'}>
                  {step.content}
                  <div className={'steps-navigation-button-wrapper'}>
                    {step.hideBackButton ? null : (
                      <Button
                        className="stepper-navigation-button"
                        onClick={async () => {
                          if (step?.onBackButtonClick) {
                            const shouldMoveToPrevStep =
                              await step.onBackButtonClick();
                            if (shouldMoveToPrevStep) {
                              goToPrevStep();
                            }
                          } else {
                            goToPrevStep();
                          }
                        }}>
                        {step?.backButtonLabel ?? t('COMMON.BACK')}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      className="stepper-navigation-button next-button"
                      onClick={async () => {
                        if (step?.onNextButtonClick) {
                          const shouldMoveToNextStep =
                            await step.onNextButtonClick();
                          if (shouldMoveToNextStep) {
                            goToNextStep();
                          }
                        } else {
                          goToNextStep();
                        }
                      }}
                      disabled={step?.nextButtonDisabledState}
                      startIcon={
                        step?.nextButtonLoadingState ? (
                          <Loader size={'1rem'} color={'inherit'} />
                        ) : null
                      }>
                      {step?.nextButtonLabel ?? t('COMMON.NEXT')}
                    </Button>
                  </div>
                </StepContent>
              </Step>
            );
          })}
        </MuiStepper>
      );
    };

    return (
      <React.Fragment>
        {renderMobileStepper()}
        {renderDesktopStepper()}
      </React.Fragment>
    );
  }
);

export default Stepper;
